import { useCallback } from "react"
import { useMutation } from "urql"

import type { SubmitProductSourceChangeInput, SubmitProductSourceChangeMutation } from "@/graphql/codegen/graphql"
import { SubmitProductSourceChangeDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseSubmitSourceChangeReturn {
  submitSourceChange: (input: SubmitProductSourceChangeInput) => Promise<boolean>
  isSubmitting: boolean
}

export const useSubmitSourceChange = (): UseSubmitSourceChangeReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    SubmitProductSourceChangeMutation,
    { input: SubmitProductSourceChangeInput }
  >(SubmitProductSourceChangeDocument)
  const submitSourceChange = useCallback(
    async (input: SubmitProductSourceChangeInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.productSourceChange.submit
      const hasError = error || output?.__typename === "SubmitProductSourceChangeFailure"

      if (hasError) {
        const message =
          output?.__typename === "SubmitProductSourceChangeFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to submit source changes. Please try again.",
        })
        return false
      }

      if (output?.__typename === "SubmitProductSourceChangeSuccess") {
        notify({ type: NotificationType.Success, message: "Source changes submitted" })
        return true
      }

      return false
    },
    [executeMutation, notify]
  )

  return { submitSourceChange, isSubmitting: fetching }
}
